import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Timeline, TimelineItem } from './ui/timeline';

const About = () => {
    const { t } = useTranslation();

    const timelineData = [
        {
            date: t('about.timeline.graduation.date'),
            title: t('about.timeline.graduation.title'),
            company: t('about.timeline.graduation.company'),
            description: t('about.timeline.graduation.description'),
            type: "education" as const
        },
        {
            date: t('about.timeline.internship1.date'),
            title: t('about.timeline.internship1.title'),
            company: t('about.timeline.internship1.company'),
            description: t('about.timeline.internship1.description'),
            type: "internship" as const
        },
        {
            date: t('about.timeline.job1.date'),
            title: t('about.timeline.job1.title'),
            company: t('about.timeline.job1.company'),
            description: t('about.timeline.job1.description'),
            type: "job" as const
        },
        {
            date: t('about.timeline.internship2.date'),
            title: t('about.timeline.internship2.title'),
            company: t('about.timeline.internship2.company'),
            description: t('about.timeline.internship2.description'),
            type: "internship" as const
        },
        {
            date: t('about.timeline.graduationEnd.date'),
            title: t('about.timeline.graduationEnd.title'),
            company: t('about.timeline.graduationEnd.company'),
            description: t('about.timeline.graduationEnd.description'),
            type: "education" as const
        },
        {
            date: t('about.timeline.freelance.date'),
            title: t('about.timeline.freelance.title'),
            company: t('about.timeline.freelance.company'),
            description: t('about.timeline.freelance.description'),
            type: "freelance" as const
        }
    ];

    return (
        <section
            id="about"
            className="py-32 relative min-h-screen flex items-center justify-center"
        >
            {/* Background decorativo estático (sem scale vinculado ao scroll).
                O overflow-hidden fica aqui, e não na <section>: os círculos
                borrados estouram a largura no mobile (causando scroll
                horizontal), mas clipar a section inteira quebraria o
                position:sticky da lua da timeline. */}
            <div className="absolute inset-0 opacity-10 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-900/30 via-purple-900/30 to-pink-900/30 blur-3xl"></div>
                <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-full blur-3xl"></div>
                <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full blur-3xl"></div>
            </div>

            <div className="container mx-auto px-4 relative z-10">
                <motion.h2
                    className="text-5xl md:text-7xl font-bold text-center text-white mb-12"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                >
                    {t('about.title')}
                </motion.h2>

                <motion.div
                    className="glass p-12 rounded-2xl max-w-5xl mx-auto shadow-2xl mb-16"
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                >
                    <motion.p 
                        className="text-lg text-white mb-6 leading-relaxed"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        viewport={{ once: true }}
                    >
                        {t('about.description1')}
                    </motion.p>
                    
                    <motion.p 
                        className="text-lg text-white mb-6 leading-relaxed"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        viewport={{ once: true }}
                    >
                        {t('about.description2')}
                    </motion.p>
                    
                    <motion.p 
                        className="text-lg text-white mb-4 leading-relaxed"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.6 }}
                        viewport={{ once: true }}
                    >
                        {t('about.description3')}
                    </motion.p>
                    
                    {/* Elementos decorativos com animação */}
                    <motion.div
                        className="flex justify-center mt-8 space-x-4"
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, delay: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <div className="w-2 h-2 bg-yellow-300 rounded-full animate-pulse"></div>
                        <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                        <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                    </motion.div>
                </motion.div>

                {/* Timeline Section */}
                <motion.div
                    className="max-w-4xl mx-auto"
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    viewport={{ once: true }}
                >
                    <h3 className="text-3xl md:text-4xl font-bold text-center text-white mb-12">
                        {t('about.trajectory')}
                    </h3>
                    
                    <Timeline>
                        {timelineData.map((item, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, x: -50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.2 }}
                                viewport={{ once: true }}
                            >
                                <TimelineItem
                                    date={item.date}
                                    title={item.title}
                                    company={item.company}
                                    description={item.description}
                                    type={item.type}
                                    isLast={index === timelineData.length - 1}
                                />
                            </motion.div>
                        ))}
                    </Timeline>
                </motion.div>
            </div>
        </section>
    );
};

export default About;
